import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import SpecRender from './SpecRender';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useRef } from 'react';
import { registerShortcut } from '../shortcuts';

const API = axios.create({ baseURL: process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:3001/api' });
API.interceptors.request.use(cfg => {
  try { const u = JSON.parse(localStorage.getItem('npd_user')); if (u?.token) cfg.headers.Authorization = 'Bearer ' + u.token; } catch {}
  return cfg;
});

export default function SpecReview({ sheetId, revId, onClose }) {
  const _u = (() => { try { return JSON.parse(localStorage.getItem('npd_user')); } catch { return null; } })();
  const canEditSpec = ['admin','standard','qc','qc_manager'].includes(_u?.role) || _u?.username === 'admin';
  const canConfirmSpec = ['admin','standard','qc_manager','op_manager','approver'].includes(_u?.role) || _u?.username === 'admin';
  const [mode, setMode] = useState('loading'); // loading | pick | review | view
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(null);
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [sheet, setSheet] = useState(null);
  const [unmatched, setUnmatched] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [revConfirmed, setRevConfirmed] = useState(false);
  const [revInfo, setRevInfo] = useState(null);
  const specRef = useRef(null);

  useEffect(() => {
    Promise.all([
      API.get('/spec-templates'),
      API.get(`/spec-sheets/${sheetId}/view/${revId}`)
    ]).then(([rt, rv]) => {
      setTemplates(rt.data);
      setSheet(rv.data.sheet);
      setRevConfirmed(!!rv.data.revision.confirmed_at);
      setRevInfo(rv.data.revision);
      if (rv.data.revision.extracted && rv.data.template) {
        setSchema(rv.data.template.schema);
        setTemplateId(rv.data.template.id);
        setValues(rv.data.revision.extracted);
        setMode(Object.keys(rv.data.revision.extracted).length ? 'view' : 'review');
      } else {
        setMode('pick');
      }
    }).catch(() => { toast.error('Load failed'); onClose(); });
  }, [sheetId, revId, onClose]);

  const runExtract = async () => {
    if (!templateId) return toast.error('Pick a template');
    setMode('loading');
    try {
      const r = await API.post(`/spec-sheets/${sheetId}/extract/${revId}`, { template_id: templateId });
      const tpl = templates.find(t => t.id === parseInt(templateId));
      setSchema(tpl.schema);
      setValues(r.data.extracted);
      setUnmatched(r.data.unmatched || []);
      toast.success(`Extracted ${Object.keys(r.data.extracted).length} fields, ${(r.data.unmatched||[]).length} need attention`);
      setMode('review');
    } catch (e) { toast.error(e.response?.data?.error || 'Extraction failed'); setMode('pick'); }
  };

  const confirmWithPdf = async () => {
    if (!specRef.current) return;
    if (!window.confirm('Confirm this revision? The rendered spec will be locked as the official PDF.')) return;
    setConfirming(true);
    try {
      const el = specRef.current;
      await Promise.all(Array.from(el.querySelectorAll('img')).map(img =>
        img.complete ? Promise.resolve() : new Promise(r => { img.onload = r; img.onerror = r; })
      ));
      const pages = el.querySelectorAll('.spec-page');
      const pdf = new jsPDF('p', 'mm', 'a4');
      for (let i = 0; i < pages.length; i++) {
        const canvas = await html2canvas(pages[i], { scale: 2, useCORS: true, allowTaint: false, imageTimeout: 20000, backgroundColor: '#ffffff' });
        const imgData = canvas.toDataURL('image/jpeg', 0.92);
        if (i > 0) pdf.addPage();
        const imgH = canvas.height * 210 / canvas.width;
        pdf.addImage(imgData, 'JPEG', 0, 0, 210, Math.min(imgH, 297));
      }
      const blob = pdf.output('blob');
      const fd = new FormData();
      fd.append('pdf', new File([blob], 'spec.pdf', { type: 'application/pdf' }));
      fd.append('rev_id', revId);
      fd.append('confirmed_by', _u?.username || '');
      await API.post(`/spec-sheets/${sheetId}/confirm-with-pdf`, fd);
      toast.success('Confirmed — official PDF generated');
      setRevConfirmed(true);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Confirm failed');
    } finally { setConfirming(false); }
  };

  useEffect(() => {
    if (mode !== 'review') return;
    return registerShortcut('save', () => saveValues());
    // eslint-disable-next-line
  }, [mode, values]);

  const saveValues = async () => {
    const r = await API.put(`/spec-sheets/${sheetId}/extracted/${revId}`, { extracted: values });
    if (r.data.new_revision) {
      toast.success('Confirmed revision preserved — changes saved as Rev ' + r.data.rev_number);
    } else {
      toast.success('Saved');
    }
    setMode('view');
  };

  const getVal = (key) => {
    const v = values[key];
    return v && typeof v === 'object' ? v.value : (v || '');
  };
  const getRef = (key) => {
    const v = values[key];
    return v && typeof v === 'object' ? (v.reference || '') : '';
  };
  const setVal = (key, val, isRef) => {
    setValues(prev => {
      const cur = prev[key];
      if (isRef || (cur && typeof cur === 'object')) {
        const obj = cur && typeof cur === 'object' ? { ...cur } : { value: cur || '', reference: '' };
        if (isRef) obj.reference = val; else obj.value = val;
        return { ...prev, [key]: obj };
      }
      return { ...prev, [key]: val };
    });
  };

  if (mode === 'loading') return (
    <div className="modal-overlay"><div className="modal" style={{ maxWidth: 400, textAlign: 'center', padding: 30 }}>Working...</div></div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 980, width: '95%', maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="modal-title" style={{ margin: 0 }}>
            {sheet?.doc_number} — {mode === 'pick' ? 'Extract Data' : mode === 'review' ? 'Review Extracted Data' : 'Specification Sheet'}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {mode === 'view' && <>
              {canConfirmSpec && !revConfirmed && (
                <button className="btn btn-primary btn-sm" onClick={confirmWithPdf} disabled={confirming}>
                  {confirming ? 'Generating PDF...' : '✅ Confirm'}
                </button>
              )}
              {canEditSpec && <button className="btn btn-secondary btn-sm" onClick={() => setMode('pick')}>🔄 Re-extract</button>}
              {canEditSpec && <button className="btn btn-secondary btn-sm" onClick={() => setMode('review')}>✏ Edit</button>}
              <button className="btn btn-secondary btn-sm" onClick={() => window.print()}>🖨 Print</button>
            </>}
            {mode === 'review' && <button className="btn btn-primary btn-sm" onClick={saveValues}>💾 Save</button>}
            <button className="btn btn-secondary btn-sm" onClick={onClose}>✕ Close</button>
          </div>
        </div>

        {mode === 'pick' && (
          <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 12, fontSize: 13 }}>Select the template format matching this document, then extract:</div>
            <select className="form-input" value={templateId || ''} onChange={e => setTemplateId(e.target.value)} style={{ maxWidth: 320 }}>
              <option value="">— Choose template —</option>
              {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={{ marginTop: 14 }}>
              <button className="btn btn-primary" onClick={runExtract}>⚙ Extract from Word Document</button>
            </div>
          </div>
        )}

        {mode === 'review' && schema && (
          <div>
            {unmatched.length > 0 && (
              <div style={{ background: '#fff3e0', border: '1px solid #ffcc80', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>
                ⚠ {unmatched.length} field(s) could not be auto-extracted — please fill manually (highlighted below)
              </div>
            )}
            {(schema.header?.fields || []).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)', borderBottom: '2px solid var(--accent)', paddingBottom: 4, marginBottom: 8 }}>Document Header</div>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '6px 10px', alignItems: 'center' }}>
                  {schema.header.fields.map(f => (
                    <React.Fragment key={f.key}>
                      <label style={{ fontSize: 12, fontWeight: 600 }}>{f.label}</label>
                      <input className="form-input" style={{ width: '100%' }} value={getVal(f.key)} onChange={e => setVal(f.key, e.target.value)}
                        placeholder={f.key === 'doc_no' ? (sheet?.doc_number || '') : f.key === 'rev_no' ? (revInfo?.rev_number || '') : ''} />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {schema.sections.map(sec => (
              <div key={sec.key} style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: 'var(--accent)', borderBottom: '2px solid var(--accent)', paddingBottom: 4, marginBottom: 8 }}>{sec.title}</div>
                <div style={{ display: 'grid', gridTemplateColumns: sec.layout === 'test_req_ref' ? '180px 1fr 220px' : '180px 1fr', gap: '6px 10px', alignItems: 'center' }}>
                  {sec.layout === 'test_req_ref' && <><div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)'}}>TEST</div><div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)'}}>REQUIREMENT</div><div style={{fontSize:10,fontWeight:700,color:'var(--text-muted)'}}>REFERENCE</div></>}
                  {sec.fields.map(f => (
                    <React.Fragment key={f.key}>
                      <label style={{ fontSize: 12, fontWeight: 600, color: unmatched.includes(f.key) && !getVal(f.key) ? '#e65100' : 'inherit' }}>{f.label}</label>
                      {f.multiline ? (
                        <textarea className="form-input" style={{ width: '100%', minHeight: 50 }} value={getVal(f.key)} onChange={e => setVal(f.key, e.target.value)} />
                      ) : (
                        <input className="form-input" style={{ width: '100%' }} value={getVal(f.key)} onChange={e => setVal(f.key, e.target.value)} />
                      )}
                      {sec.layout === 'test_req_ref' && (
                        <input className="form-input" style={{ width: '100%' }} value={getRef(f.key)} onChange={e => setVal(f.key, e.target.value, true)} placeholder="Reference standard" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            ))}
            <button className="btn btn-primary" onClick={saveValues}>💾 Save Reviewed Data</button>
          </div>
        )}

        {mode === 'view' && schema && (() => {
          const sysVals = {
            doc_no: sheet?.doc_number || '',
            rev_no: revInfo?.rev_number || '',
            issued_date: revInfo?.confirmed_at ? new Date(revInfo.confirmed_at).toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB'),
            department: 'R&D'
          };
          const merged = { ...sysVals, ...Object.fromEntries(Object.entries(values).filter(([k, v]) => v !== '' && v !== null && v !== undefined)) };
          if (merged.__product_image) merged.__product_image_url = '/api/spec-images/' + merged.__product_image;
          return (
          <div ref={specRef}>
          <SpecRender schema={schema} values={merged}
            docTitle={(sheet?.doc_number || '') + (getVal('product_name') ? ' — ' + getVal('product_name') : '')} />
          </div>
          );
        })()}
      </div>
    </div>
  );
}
